"use client";

import { useEffect } from "react";
import { gridMetrics } from "@/components/hero/HeroGrid";

/**
 * Sizes every [data-grid-block] in the surrounding <section> to WHOLE grid
 * rows, one row taller than its natural content (two for blocks carrying
 * `data-tall`, zero extra for `data-snug` — opaque cards whose own chrome
 * already insets their content only need the whole-row snap), with the
 * content vertically centered via symmetric inline padding — the grid-paper
 * rule "blocks span whole rows one row taller than their content, content
 * centered, never flush to a clearing edge".
 *
 * Because the resulting block height is an exact row multiple, a block whose
 * top sits on a row line (useBandRowAlign) also ENDS on a row line — the
 * clearing has no snap tail, so the visible gap between consecutive blocks
 * is always exactly the free row(s) the aligner guarantees, never "one row
 * here, almost two there".
 *
 * Pass a ref to any element inside the section. Desktop-only (<768px clears
 * the inline padding). Re-runs on resize + fonts.ready and dispatches a
 * change-guarded grid-relayout so useBandRowAlign/GridClearing re-measure.
 * Deliberately does NOT listen to grid-relayout itself: alignment shifts
 * block positions, never their content heights.
 */
export default function useWholeRowBlocks(innerRef: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    let raf = 0;
    const layout = () => {
      raf = 0;
      const section = innerRef.current?.closest("section");
      if (!section) return;
      const blocks = Array.from(section.querySelectorAll<HTMLElement>("[data-grid-block]"));
      if (!blocks.length) return;
      const prevKey = blocks.map((b) => `${b.style.paddingTop}|${b.style.paddingBottom}`).join(";");
      blocks.forEach((b) => {
        b.style.paddingTop = "";
        b.style.paddingBottom = "";
      });
      if (document.documentElement.clientWidth >= 768) {
        const { cell } = gridMetrics(document.documentElement.clientWidth);
        blocks.forEach((b) => {
          const h = b.getBoundingClientRect().height;
          const extra = b.hasAttribute("data-tall") ? 2 : b.hasAttribute("data-snug") ? 0 : 1;
          const pad = (Math.ceil(h / cell - 0.01) + extra) * cell - h;
          // Add ON TOP of any class padding (e.g. a card's p-5): the inline
          // value REPLACES the computed one, so writing the correction alone
          // would mostly cancel against the base instead of growing the block.
          const cs = getComputedStyle(b);
          const basePt = parseFloat(cs.paddingTop) || 0;
          const basePb = parseFloat(cs.paddingBottom) || 0;
          b.style.paddingTop = `${basePt + pad / 2}px`;
          b.style.paddingBottom = `${basePb + pad / 2}px`;
        });
      }
      const nextKey = blocks.map((b) => `${b.style.paddingTop}|${b.style.paddingBottom}`).join(";");
      if (nextKey !== prevKey) window.dispatchEvent(new Event("grid-relayout"));
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(layout);
    };
    layout();
    document.fonts.ready.then(schedule).catch(() => {});
    window.addEventListener("resize", schedule);
    // Content heights can change without a resize (late font swaps, tab rows
    // un-wrapping) — stale paddings then break the whole-row guarantee. The
    // observer settles: re-running an already-correct layout writes identical
    // values in the same frame, so it fires no further size change.
    const section = innerRef.current?.closest("section");
    const ro = section ? new ResizeObserver(schedule) : null;
    if (section && ro) ro.observe(section);
    return () => {
      window.removeEventListener("resize", schedule);
      ro?.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [innerRef]);
}
